import { Component, Input, isDevMode, OnDestroy, OnInit } from '@angular/core'
import { NgClass, NgStyle, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { take, takeUntil } from 'rxjs'
import { isEqual } from 'lodash-es'

import { ApplicationSetting, StudyService } from '@nx-superprep/backend/firebase'
import { ResponsiveComponent } from '../responsive.component'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { MatSliderModule } from '@angular/material/slider'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { FormsModule } from '@angular/forms'

interface Slider {
  name: string,
  title: string,
  value: number,
  labels: string[],
  label: string,
}

interface Toggle {
  name: string,
  title: string,
  active: boolean,
}

@Component({
  selector: 'nx-superprep-settings',
  standalone: true,
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  imports: [
    NgClass, NgStyle, TitleCasePipe, NgTemplateOutlet,
    MatSliderModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
  ],
})
export class SettingsComponent extends ResponsiveComponent implements OnInit, OnDestroy {
  @Input() moduleId: string|undefined

  notifications = false

  get isModal() {
    return !!this.moduleId
  }

  private _setting!: ApplicationSetting
  get setting() {
    return this._setting
  }
  set setting(value) {
    this._setting = value
    this.sliders = [
      this.sliderFor('duration', 'Duration', value.studySetting.duration, ['Short', 'Condensed', 'Normal', 'Extended', 'Long']),
      this.sliderFor('difficulty', 'Difficulty', value.studySetting.difficulty, ['Casual', 'Easy', 'Normal', 'Hard', 'Intense']),
    ]
    if (this.moduleId) {
      this.sliders.push(this.sliderFor('volume', 'Volume', this.volume, ['Muted', '25%', '50%', '75%', '100%']))
    }
    this.slides = [
      { name: 'soundEffect', title: 'Sound Fx', active: value.studySetting.soundEffect },
      { name: 'animation', title: 'Animations', active: value.studySetting.animation }
    ]
    if (isDevMode()) { // always allow toggling in dev mode
      this.slides.push(
        { name: 'contentReviewMode', title: 'Content Review', active: value.contentReviewMode },
      )
      this.slides.push(
        { name: 'debugMode', title: 'Debug', active: value.debugMode }
      )
      this.slides.push(
        { name: 'notifications', title: 'Notifications', active: this.notifications }
      )
    }
  }

  sliderFor(name: string, title: string, value: number, labels: string[]) {
    return ({ name, title, value, labels, label:labels[value] })
  }

  slides: Toggle[] = []
  sliders: Slider[] = []

  get isNavigationVisible() { return !!this.moduleId }
  get title() { return this.isNavigationVisible ? "Study Style" : 'Daily Study Style' }

  set volume(value: number) { Howler.volume(value / 5) }
  get volume() { return 5 * Howler.volume() }

  onClose() {
    this.bsModalRef?.onHide?.emit()
    this.bsModalRef?.hide()
    if (this.moduleId) {
      this.router.navigate([this.studyService.activeLesson ? '/study' : '/quiz', this.moduleId], { replaceUrl:true, skipLocationChange: true })
    }
  }

  override ngOnInit() {
    super.ngOnInit()
    this.studyService.getAppSetting()
    .pipe(takeUntil(this.onDestroy))
    .subscribe((setting: ApplicationSetting) => {
      if (!isEqual(this.setting, setting)) { this.setting = setting }
    })
  }

  async onToggle(slide: Toggle) {
    if (!this.setting) { return }
    slide.active = !slide.active
    const change: { animation?: string, soundEffect?: string } = {}
    if (slide.name === 'animation') {
      change.animation = slide.active ? 'on' : 'off'
      this.setting.studySetting.animation = slide.active
    }
    if (slide.name === 'soundEffect') {
      change.soundEffect = slide.active ? 'on' : 'off'
      this.setting.studySetting.soundEffect = slide.active
    }
    if (isDevMode()) { // always allow toggling in dev mode
      if (slide.name === 'contentReviewMode') { this.setting.contentReviewMode = slide.active }
      if (slide.name === 'debugMode') { this.setting.debugMode = slide.active }
    }
    await this.studyService.setSetting(this.setting, change)
  }

  onSliderChange(slider: Slider) {
    if (!this.setting) { return }
    slider.label = slider.labels[slider.value]
    if (slider.name === 'volume') { this.volume = slider.value; return }
    const change: { duration?: string, difficulty?: string } = {}
    if (slider.name === 'duration' && this.setting.studySetting.duration !== slider.value) {
      change.duration = this.setting.studySetting.duration < slider.value ? 'increase' : 'decrease'
      this.setting.studySetting.duration = slider.value
    }
    else if (slider.name === 'difficulty' && this.setting.studySetting.difficulty !== slider.value) {
      change.difficulty = this.setting.studySetting.difficulty < slider.value ? 'increase' : 'decrease'
      this.setting.studySetting.difficulty = slider.value
    }
    this.studyService.updateDayPlanModulesAndSetting(this.setting, change)
    .pipe(take(1))
    .subscribe(async () => await this.studyService.setSetting(this.setting, change))
  }

  constructor(
    public bsModalRef: BsModalRef,
    protected studyService: StudyService,
  ) {
    super()
    super.rootClassName = 'settings-root-class-name'
  }
}
